import {
  NgZone,
  Component,
  Output,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  PageTitle,
  RoutesEnum,
  TimeCompositionListEnum,
} from '@constants/global.enum';
import {
  newBeginningsHeaders,
  workedOnHeaders,
  workingOnHeaders,
} from './team-composition-details.config';
import { iconSizeInRem } from '@shared/config/allowedKeys.config';
import { SearchService } from '@services/search.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TeamManagementService } from '../team-management.service';
import { GlobalErrorService } from '@services/global-error.service';
import {
  Project,
  TeamCompositionDetails,
  TeamCompositionEvents,
  TeamCompositionList,
} from '@interfaces/team-management.model';
import { AuthService } from '@core/auth.service';
import { ToasterService } from '@services/toaster.service';
import { capitalizeWords, getInitials } from '@utils/utils';
import { dropdownValues } from '@interfaces/common.model';
import { ProductivityService, ProductivityRecord } from '@app/pages/productivity/productivity.service';
import { IskillApiService } from '@app/pages/iskill/services/iskill-api.service';

/** Shape of GET /people/{id}/skill-summary (iSkill BE) — kept local to this
 *  page; the iSkill feature models stay owned by the iskill module. */
interface SkillSummary {
  employeeId: string;
  hasAssessment: boolean;
  totalRatedSkills: number;
  ratingDistribution: {
    id: string;
    label: string;
    count: number;
    bgColor: string;
    textColor: string;
  }[];
  compositeScore: number | null;
  benchmarkPercent: number | null;
  aiLevel: string | null;
}

/** One SVG stroke segment of the skills donut. */
interface DonutSegment {
  color: string;
  dasharray: string;
  dashoffset: number;
  label: string;
  count: number;
}

@Component({
  selector: 'app-team-composition-details',
  templateUrl: './team-composition-details.component.html',
  styleUrl: './team-composition-details.component.scss',
})
export class TeamCompositionDetailsComponent implements OnChanges {
  @Input() empId: string | null = null;
  @Output() close = new EventEmitter<void>();
  showContractorProfileModal: boolean = false;
  selectedContractorId: string = '';
  breadcrumb = RoutesEnum;
  heading = PageTitle;
  TimeCompositionListLabels = TimeCompositionListEnum;
  fontSize_opentab = iconSizeInRem;
  monthAttendance: { month: string; days: number }[] = [];
  editorChoiceIcon = 'assets/icons/editor-choice-icon.svg';
  imageSrc = 'assets/img/no-data-found.png';
  employeeId: string | null = null;
  showChangeTeamModal = false;
  reporteeData: dropdownValues[] = [];
  selectedDropdownValue: { employeeId: string; filterType?: string } | null =
    null;
  selectedDropdownDisplayValue: string = 'all';
  selectedDropdownObject: dropdownValues | null = null;

  workingOnHeaders = workingOnHeaders;
  workedOnHeaders = workedOnHeaders;
  currentPsId: string = '';
  projectData: { workingOn: Project[]; workedOn: Project[] } = {
    workingOn: [],
    workedOn: [],
  };
  activeProfile: TeamCompositionList | null = null;
  filterValue = '';
  displayData: TeamCompositionList[] = [];
  profiles: TeamCompositionList[] = [];
  isWorkedOnExpanded = false;
  isDataLoaded = false;
  employeeData: TeamCompositionDetails | null = null;
  selectedFilter = 'all';
  showNewBeginningsModal = false;
  newBeginningsHeaders = newBeginningsHeaders;
  beginningsData: TeamCompositionEvents[] = [];

  /** People picker drawer — overlays the 360 instead of reserving a column.
   *  Closed by default so the 360 always renders full-width. */
  peopleDrawerOpen = false;

  skillSummary: SkillSummary | null = null;
  skillSummaryLoading = false;
  donutSegments: DonutSegment[] = [];
  /** Circumference of the donut's r=42 circle — segments are dash lengths on it. */
  readonly donutCircumference = 2 * Math.PI * 42;

  productivityRecords: ProductivityRecord[] = [];
  productivityLoading = false;
  availableMetrics: { metricId: string; metricName: string; recordCount: number }[] = [];
  selectedMetricId: string | null = null;
  productivityChartData: {
    labels: string[];
    datasets: {
      label: string;
      data: (number | null)[];
      borderColor: string;
      backgroundColor: string;
      borderDash?: number[];
      tension: number;
    }[];
  } = { labels: [], datasets: [] };
  productivityChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 18,
          boxHeight: 3,
          padding: 14,
          font: { size: 11 },
        },
      },
      tooltip: { mode: 'index', intersect: false },
      datalabels: { display: false },
    },
    interaction: { mode: 'nearest', axis: 'x', intersect: false },
    elements: {
      point: { radius: 3, hoverRadius: 5 },
      line: { borderWidth: 2 },
    },
    scales: {
      y: { beginAtZero: true, grid: { color: '#f3f4f6' } },
      x: { grid: { display: false } },
    },
  };

  constructor(
    private searchService: SearchService,
    private router: Router,
    private route: ActivatedRoute,
    private teamManagementService: TeamManagementService,
    private errorHandler: GlobalErrorService,
    private authService: AuthService,
    private toaster: ToasterService,
    private productivityService: ProductivityService,
    private iskillApi: IskillApiService,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    // Deep link: /team-management/team-composition?empId=… opens that
    // person's 360 directly (used by iSkill's "360° view" button).
    const qpEmpId = this.route.snapshot.queryParamMap.get('empId');
    if (qpEmpId && !this.empId) this.empId = qpEmpId;

    // Subscribe to cached reporteeData from service
    this.teamManagementService.getReporteeData().subscribe((data) => {
      if (data && data.length > 0) {
        this.reporteeData = data;
      }
    });

    // Subscribe to selected dropdown value from service
    this.teamManagementService
      .getSelectedDropdownValue()
      .subscribe((selection) => {
        this.selectedDropdownValue = selection;
        this.syncSelectedDropdownDisplayValue(selection);
      });

    this.authService.getEmployeeDetails().then((employeeData) => {
      this.employeeId = employeeData?.employeeId || null;
      this.syncSelectedDropdownDisplayValue(this.selectedDropdownValue);

      if (this.employeeId) {
        // Use the selected dropdown value if available, otherwise use employeeId
        const employeeIdToUse =
          this.selectedDropdownValue?.employeeId || this.employeeId;
        const filterType = this.selectedDropdownValue?.filterType;
        this.loadTeamCompositionList(employeeIdToUse, filterType);
      }
      if (this.empId) {
        this.loadTeamCompositionData(this.empId);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['empId'] && changes['empId'].currentValue) {
      this.loadTeamCompositionData(changes['empId'].currentValue);
    }
  }

  loadTeamCompositionList(empId: string, filterType?: string) {
    // Clear existing data before loading new data
    this.profiles = [];
    this.displayData = [];
    this.activeProfile = null;

    this.teamManagementService
      .getTeamCompositionList(empId, filterType)
      .subscribe({
        next: (res) => {
          this.profiles = (res?.data || []).sort((a, b) =>
            a.name.localeCompare(b.name),
          );
          this.displayData = this.profiles;
          const navEmpId = this.empId;
          if (navEmpId) {
            this.activeProfile =
              this.profiles?.find((profile) => profile.empId === navEmpId) ||
              null;
          }
          if (!this.activeProfile && this.profiles.length > 0) {
            this.activeProfile = this.profiles[0];
            this.loadTeamCompositionData(this.profiles[0].empId);
          }
        },
        error: (err) => {
          this.errorHandler.logComponentError(err);
        },
      });
  }

  loadTeamCompositionData(psId: string) {
    this.teamManagementService.getTeamCompositionDetails(psId).subscribe({
      // zone.run: the ?empId= deep-link chain passes through the Okta token
      // promise and can land outside the Angular zone — without re-entering,
      // the banner/360 don't paint until the next click.
      next: (res) => this.zone.run(() => {
        if (res.status === 'SUCCESS') {
          this.employeeData = res?.data;
          this.currentPsId = res?.data?.psId;
          this.beginningsData = res?.data?.events;
          this.isDataLoaded = true;
          this.monthAttendance = this.calculateMonthAttendance(
            this.employeeData?.rtoDatesLast12Months || '',
          );
          // Productivity / skills / RTO now render inside the embedded
          // Employee 360, which fetches its own data per selection.
          // future features
          // this.fetchProjectData(psId);
        } else {
          this.employeeData = null;
          this.monthAttendance = [];
          this.isDataLoaded = true;
          this.toaster.showError(res?.message, 'Error');
        }
      }),
      error: (err) => this.zone.run(() => {
        this.isDataLoaded = true;
        this.errorHandler.logComponentError(err);
      }),
    });
  }

  // future features

  // fetchProjectData(empId: string) {
  //   this.teamManagementService.getProjectData(empId).subscribe({
  //     next: (projects) => {
  //       if (projects && Array.isArray(projects)) {
  //         this.projectData.workingOn = projects?.filter(
  //           (p) => p.endDate === null,
  //         );
  //         this.projectData.workedOn = projects?.filter(
  //           (p) => p.endDate !== null,
  //         );
  //       }
  //     },
  //     error: (err) => {
  //       this.projectData = { workingOn: [], workedOn: [] };
  //       this.errorHandler.logComponentError(err);
  //     },
  //   });
  // }

  calculateMonthAttendance(
    rtoDatesStr: string,
  ): { month: string; days: number }[] {
    const months: { month: string; key: string }[] = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const label = d.toLocaleString('default', {
        month: 'short',
        year: 'numeric',
      });
      const key = `${d.getFullYear()}-${('0' + (d.getMonth() + 1)).slice(-2)}`;
      months.push({ month: label, key });
    }
    const attendanceMap: { [key: string]: number } = {};
    const rtoDates = rtoDatesStr ? rtoDatesStr.split(',') : [];
    rtoDates.forEach((dateStr) => {
      const date = new Date(dateStr);
      const key = `${date.getFullYear()}-${('0' + (date.getMonth() + 1)).slice(-2)}`;
      attendanceMap[key] = (attendanceMap[key] || 0) + 1;
    });
    return months.map((m) => ({
      month: m.month,
      days: attendanceMap[m.key] || 0,
    }));
  }

  clearInput() {
    this.displayData = this.profiles;
    this.filterValue = '';
  }

  search(event: KeyboardEvent) {
    this.filterValue = this.searchService.getFilteredValue(event);
    this.displayData = this.profiles?.length
      ? this.searchService.getSearchedData(
          this.profiles,
          this.filterValue?.toLowerCase(),
        )
      : [];
  }

  openPeopleDrawer(): void { this.peopleDrawerOpen = true; }
  closePeopleDrawer(): void { this.peopleDrawerOpen = false; }

  /** Team labels for the searchable dropdown (labels shown, values mapped
   *  back in onTeamPick — the searchable-select works on plain strings).
   *  The 'all' option is excluded: the dropdown's built-in clear row
   *  ("All Teams" placeholder) already covers it, and duplicating the label
   *  showed "All Teams" twice in the panel. */
  get teamOptions(): string[] {
    return this.reporteeData
      .filter((o) => o.value !== 'all')
      .map((o) => o.label);
  }

  /** Label of the currently selected team option, '' when none/All. */
  get selectedTeamLabel(): string {
    const match = this.reporteeData.find(
      (o) => o.value === this.selectedDropdownDisplayValue,
    );
    // 'all' reads better as the placeholder than as a selected value.
    if (!match || match.value === 'all') return '';
    return match.label;
  }

  /** Bridge the searchable dropdown (string labels) onto the existing
   *  value-based filter flow. Empty label = cleared = All Teams. */
  onTeamPick(label: string): void {
    const option = this.reporteeData.find((o) => o.label === label);
    this.onFilterChange(option ? option.value : 'all');
  }

  onFilterChange(event: any) {
    const selectedValue = event?.value || event;

    // Find and store the complete selected object from reporteeData
    this.selectedDropdownObject =
      this.reporteeData.find((option) => option.value === selectedValue) ||
      null;
    this.selectedDropdownDisplayValue = selectedValue;

    let filterType: string | undefined;
    let employeeIdToUse: string;

    // Determine filterType and employeeId based on selection
    if (selectedValue === 'dr') {
      // Direct Reportees - add filterType
      filterType = 'DirectReportee';
      employeeIdToUse = this.employeeId || '';
      this.teamManagementService.setSelectedDropdownValue({
        employeeId: employeeIdToUse,
        filterType: filterType,
      });
    } else if (selectedValue === 'all') {
      // All Teams - no filterType
      filterType = undefined;
      employeeIdToUse = this.employeeId || '';
      this.teamManagementService.setSelectedDropdownValue({
        employeeId: employeeIdToUse,
      });
    } else {
      // Specific employee - no filterType
      filterType = undefined;
      employeeIdToUse = selectedValue;
      this.teamManagementService.setSelectedDropdownValue({
        employeeId: selectedValue,
      });
    }

    // Reload data based on selection with the correct parameters
    this.loadTeamCompositionList(employeeIdToUse, filterType);
  }

  private syncSelectedDropdownDisplayValue(
    selection: { employeeId: string; filterType?: string } | null,
  ): void {
    if (!selection) {
      this.selectedDropdownDisplayValue = 'all';
      return;
    }

    if (selection.filterType === 'DirectReportee') {
      this.selectedDropdownDisplayValue = 'dr';
      return;
    }

    if (this.employeeId && selection.employeeId === this.employeeId) {
      this.selectedDropdownDisplayValue = 'all';
      return;
    }

    this.selectedDropdownDisplayValue = selection.employeeId;
  }

  copyContent(text: string | null): void {
    if (text) {
      navigator.clipboard.writeText(text);
    }
  }

  onProfileClick(profile: TeamCompositionList): void {
    this.activeProfile = profile;
    this.loadTeamCompositionData(profile.empId);
    // Picking a person is the drawer's job done — get out of the way.
    this.closePeopleDrawer();
  }

  getAttendanceColorClass(days: number): string {
    if (days === 0) return 'exempted';
    if (days < 8) return 'remote';
    if (days >= 8 && days < 12) return 'partial-hybrid';
    if (days === 12) return 'hybrid';
    if (days > 12) return 'regular';
    return '';
  }

  toggleWorkedOnSection() {
    this.isWorkedOnExpanded = !this.isWorkedOnExpanded;
  }

  navigateToContractorProfile(contractorId: string | null): void {
    if (contractorId) {
      this.router.navigate(
        ['/vendor-management/contractor-profile', contractorId],
        {
          queryParams: { source: 'team-composition' },
        },
      );
    }
  }

  getInitials(name: string): string {
    return getInitials(name);
  }

  // future features
  // openChangeTeamModal() {
  //   this.showChangeTeamModal = true;
  // }

  closeChangeTeamModal() {
    this.showChangeTeamModal = false;
  }

  capitalizeWords(text: string): string {
    return capitalizeWords(text);
  }

  handleChangeTeamSubmitted() {
    // future features
    // this.fetchProjectData(this.currentPsId);
    this.closeChangeTeamModal();
  }
  onClose() {
    this.selectedContractorId = '';
    this.close.emit();
  }

  openContractorProfile(contractorId: string): void {
    if (contractorId) {
      this.selectedContractorId = contractorId;
      this.showContractorProfileModal = true;
      const selectedProfile = this.profiles?.find(
        (profile) => profile?.empId === contractorId,
      );
      if (selectedProfile) {
        this.activeProfile = selectedProfile;
      }
      // Push a synthetic history entry so browser Back closes the modal and
      // returns to Team Composition — without this, Back falls through past
      // the modal (which never touched the URL) and lands on Team Dashboard.
      if (typeof history !== 'undefined') {
        history.pushState({ teamCompositionModal: true }, '');
      }
    }
  }

  /** Catch the browser Back press while the contractor-profile modal is
   *  open on this page — close the modal and stay on Team Composition,
   *  matching what the manager expects when they came in from a row click. */
  @HostListener('window:popstate')
  onPopState(): void {
    if (this.showContractorProfileModal) {
      this.showContractorProfileModal = false;
      this.selectedContractorId = '';
    }
  }

  createNewRequest(): void {
    this.showNewBeginningsModal = true;
  }

  closeNewBeginningsModal(): void {
    this.showNewBeginningsModal = false;
  }

  handleNewBeginnings(): void {
    this.loadTeamCompositionData(this.currentPsId);
    this.showNewBeginningsModal = false;
  }

  private loadProductivity(employeeId: string): void {
    this.productivityLoading = true;
    this.productivityRecords = [];
    this.availableMetrics = [];
    this.selectedMetricId = null;
    this.productivityChartData = { labels: [], datasets: [] };
    this.productivityService.recordsForEmployee(employeeId, 6).subscribe({
      next: (records) => {
        this.productivityRecords = records;
        this.availableMetrics = this.summarizeMetrics(records);
        this.selectedMetricId = this.pickDefaultMetric(this.availableMetrics);
        this.productivityChartData = this.buildProductivityChart(records, this.selectedMetricId);
        this.productivityLoading = false;
      },
      error: (err) => {
        this.productivityLoading = false;
        this.errorHandler.logComponentError(err);
      },
    });
  }

  onSelectMetric(metricId: string): void {
    this.selectedMetricId = metricId;
    this.productivityChartData = this.buildProductivityChart(this.productivityRecords, metricId);
  }

  // ── Skills tile (iSkill skill-summary) ──────────────────────────────────

  private loadSkillSummary(employeeId: string): void {
    this.skillSummaryLoading = true;
    this.skillSummary = null;
    this.donutSegments = [];
    this.iskillApi.get<SkillSummary>(`/people/${employeeId}/skill-summary`).subscribe({
      next: (res) => {
        this.skillSummaryLoading = false;
        if (res.status === 'SUCCESS' && res.data) {
          this.skillSummary = res.data;
          this.donutSegments = this.buildDonutSegments(res.data);
        }
      },
      error: (err) => {
        // Tile is a nice-to-have embed — degrade to the empty state quietly
        // rather than toasting over the whole Team Management page.
        this.skillSummaryLoading = false;
        this.errorHandler.logComponentError(err);
      },
    });
  }

  /** Convert rating counts into SVG stroke segments on the r=42 circle.
   *  Each segment is a dash of `count/total` of the circumference, offset by
   *  the cumulative length of the segments before it (negative offset walks
   *  clockwise from 12 o'clock, matching the -90° rotation in the template). */
  private buildDonutSegments(summary: SkillSummary): DonutSegment[] {
    const total = summary.totalRatedSkills;
    if (!total) return [];
    const C = this.donutCircumference;
    let cumulative = 0;
    return summary.ratingDistribution
      .filter((s) => s.count > 0)
      .map((s) => {
        const len = (s.count / total) * C;
        const seg: DonutSegment = {
          color: s.bgColor,
          dasharray: `${len} ${C - len}`,
          dashoffset: -cumulative,
          label: s.label,
          count: s.count,
        };
        cumulative += len;
        return seg;
      });
  }

  /** Slices with counts, for the legend (skips zero-count ratings). */
  get skillLegend(): { id: string; label: string; count: number; bgColor: string }[] {
    return (this.skillSummary?.ratingDistribution || []).filter((s) => s.count > 0);
  }

  goToIskillProfile(): void {
    if (!this.currentPsId) return;
    // view=team is required for the shell nav's Team Explorer item to light
    // up — its active check exact-matches that param (see iskill-nav.config).
    this.router.navigate(['/iskill'], {
      queryParams: { view: 'team', tab: 'profile', employeeId: this.currentPsId },
    });
  }

  private summarizeMetrics(records: ProductivityRecord[]) {
    const byMetric = new Map<string, { metricId: string; metricName: string; recordCount: number }>();
    for (const r of records) {
      const existing = byMetric.get(r.metricId);
      if (existing) {
        existing.recordCount++;
      } else {
        byMetric.set(r.metricId, { metricId: r.metricId, metricName: r.metricName, recordCount: 1 });
      }
    }
    return Array.from(byMetric.values()).sort((a, b) => b.recordCount - a.recordCount);
  }

  private pickDefaultMetric(metrics: { metricId: string; metricName: string; recordCount: number }[]): string | null {
    if (!metrics.length) return null;
    // Preferred defaults per role — first match wins
    const preferred = ['sprint-velocity', 'pi-delivery-individual', 'backlog-health'];
    const found = preferred.find((id) => metrics.some((m) => m.metricId === id));
    return found ?? metrics[0].metricId;
  }

  private buildProductivityChart(records: ProductivityRecord[], metricId: string | null) {
    if (!records.length || !metricId) {
      return { labels: [], datasets: [] };
    }
    const forMetric = records.filter((r) => r.metricId === metricId);
    if (!forMetric.length) {
      return { labels: [], datasets: [] };
    }
    const metricName = forMetric[0].metricName;
    const periods = Array.from(new Set(forMetric.map((r) => r.periodId)))
      .sort((a, b) => this.periodSortKey(a) - this.periodSortKey(b));
    const labels = periods.map((p) => this.periodLabel(p));
    const color = '#006A4D';
    return {
      labels,
      datasets: [
        {
          label: `${metricName} · Actual`,
          data: periods.map((p) => forMetric.find((r) => r.periodId === p)?.actual ?? null),
          borderColor: color,
          backgroundColor: color,
          tension: 0.3,
        },
        {
          label: `${metricName} · Baseline`,
          data: periods.map((p) => forMetric.find((r) => r.periodId === p)?.baseline ?? null),
          borderColor: color,
          backgroundColor: color,
          borderDash: [4, 4],
          tension: 0.3,
        },
      ],
    };
  }

  private periodLabel(periodId: string): string {
    // 'AUG_2026' → 'Aug 2026'
    const parts = periodId.split('_');
    if (parts.length !== 2) return periodId;
    const [month, year] = parts;
    return `${month.charAt(0)}${month.substring(1).toLowerCase()} ${year}`;
  }

  private periodSortKey(periodId: string): number {
    // 'JUL_2026' → 202607, 'PI_2026_Q3' → 202600 (falls back so PI groups first)
    const parts = periodId.split('_');
    if (parts.length !== 2) return 0;
    const [month, year] = parts;
    const months: Record<string, number> = {
      JAN: 1, FEB: 2, MAR: 3, APR: 4, MAY: 5, JUN: 6,
      JUL: 7, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12,
    };
    const m = months[month.toUpperCase()] ?? 0;
    return Number(year) * 100 + m;
  }
}
